export type PixelCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Converte URL remota em object URL same-origin para o canvas de crop
 * (evita SecurityError em imagens cross-origin / canvas tainted).
 */
export async function loadRemoteImageForCrop(url: string): Promise<string> {
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }

  const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!response.ok) {
    throw new Error('Não foi possível carregar a imagem para recorte.');
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export function revokeCropObjectUrl(url: string | null | undefined) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/** Usa o mesmo <img> exibido no ReactCrop para calcular o recorte corretamente. */
export async function getCroppedImageBlobFromElement(
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
  mimeType = 'image/jpeg',
  quality = 0.92,
): Promise<Blob> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const cropX = pixelCrop.x * scaleX;
  const cropY = pixelCrop.y * scaleY;
  const cropWidth = pixelCrop.width * scaleX;
  const cropHeight = pixelCrop.height * scaleY;

  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(cropWidth);
  canvas.height = Math.floor(cropHeight);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Não foi possível preparar o recorte da imagem.');
  }

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Falha ao gerar a imagem recortada.'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}
